import { createSelector } from 'reselect'
import { isSpoofed } from 'selectors/isSpoofed'
import { checkoutAccommodationOfferView } from '../../view/accommodation'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { RESERVE_FOR_ZERO_PAYMENT_SCHEDULE_TEMPLATE_ID } from 'constants/payment'
import config from 'constants/config'
import { isLEHotelItem, isNoProtectionInsuranceItem, findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import getAllItemsDiscountTotals from '../getAllItemsDiscountTotals'
import { checkoutWithMemberPrice } from 'checkout/selectors/view/luxPlusSubscription'
import { getInsuranceTotals } from '../insurance'
import { getAccommodationBreakdownView } from 'checkout/selectors/view/accommodation'
import { getPaymentScheduleKey } from 'lib/payment/paymentScheduleUtils'
import isPaymentScheduleEnabled from './isPaymentScheduleEnabled'
import { buildPaymentScheduleItems, calculateGiftCardAmount } from './paymentScheduleItemUtils'

type ItemView = App.Checkout.AccommodationItemView

function isValidReserveForZeroOrder(cartItems: Array<App.Checkout.AnyItem>): boolean {
  const reservableItems = cartItems.filter(item => !isNoProtectionInsuranceItem(item))
  // false if there are no reservable items, or if any of the items are not LE hotel items (e.g. activities, packages with non-hotel components)
  if (!reservableItems.length || !reservableItems.every(isLEHotelItem)) {
    return false
  }

  const offerIds = new Set(reservableItems.map(item => item.offerId))
  return offerIds.size === 1
}

export const isReserveForZeroPaymentScheduleFlagOn = createSelector(
  (state: App.State) => isSpoofed(state),
  (_state: App.State) => config.RESERVE_FOR_ZERO_PAYMENT_SCHEDULE_ENABLED,
  (isSpoofed, flagEnabled) => isSpoofed && flagEnabled,
)

export const isReserveForZeroPaymentScheduleAvailable = createSelector(
  isReserveForZeroPaymentScheduleFlagOn,
  (state: App.State) => isPaymentScheduleEnabled(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => state.checkout.cart.items,
  (
    isFlagOn,
    paymentScheduleEnabled,
    postPurchase,
    isGift,
    accommodationOfferView,
    cartItems,
  ) => {
    if (!isFlagOn || !paymentScheduleEnabled || postPurchase || isGift) {
      return false
    }
    // check the offer
    if (!accommodationOfferView.data.every(view =>
      view.offer?.type === OFFER_TYPE_HOTEL &&
      view.offer.reserveForZero &&
      view.itemViews.some((itemView: ItemView) =>
        isPackageEligibleForZeroReservation(view.offer!, itemView.totals.isReservableForZero, moment(view.startDate)),
      ))) {
      return false
    }

    // if the offer is valid, check the items in the order
    return isValidReserveForZeroOrder(cartItems)
  },
)

export const getReserveForZeroPaymentScheduleRequestInfo = createSelector(
  isReserveForZeroPaymentScheduleAvailable,
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (state: App.State) => getAllItemsDiscountTotals(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => getInsuranceTotals(state),
  (state: App.State) => getAccommodationBreakdownView(state),
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.checkout.payment.useCredit,
  (state: App.State) => state.checkout.payment.useGiftCard,
  (state: App.State) => state.redeemedGiftCards.cards,
  (state: App.State) => checkoutAccommodationOfferView(state),
  (
    isAvailable,
    cartItems,
    offers,
    discountTotalToCartItemsMap,
    checkoutWithMemberPrice,
    insuranceTotals,
    accommodationBreakdownView,
    currency,
    isCreditApplied,
    isGiftCardApplied,
    redeemedGiftCards,
    accommodationOfferView,
  ): App.Checkout.PaymentScheduleRequestInfo | null => {
    if (!isAvailable) {
      return null
    }

    const hotelItem = cartItems.find(isLEHotelItem)
    if (!hotelItem) {
      return null
    }

    const startDate = accommodationOfferView.data[0]?.startDate
    if (!startDate) {
      return null
    }

    const giftCardAmount = calculateGiftCardAmount(redeemedGiftCards)

    const items = buildPaymentScheduleItems(cartItems, {
      offers,
      discountTotalToCartItemsMap,
      checkoutWithMemberPrice,
      insuranceTotals,
      accommodationBreakdownView,
    })

    if (!items) {
      return null
    }

    return {
      templateId: RESERVE_FOR_ZERO_PAYMENT_SCHEDULE_TEMPLATE_ID,
      bookingInfo: {
        bookingDate: moment().format(ISO_DATE_FORMAT),
        checkinDate: startDate,
        supplierDueDate: startDate,
        isCreditApplied,
        isGiftCardApplied,
        giftCardAmount,
        isPaymentScheduleRebooking: false,
        currency,
        items,
      },
    }
  },
)

export const getReserveForZeroPaymentSchedule = createSelector(
  (state: App.State) => state.paymentSchedule,
  getReserveForZeroPaymentScheduleRequestInfo,
  (paymentSchedule, reserveForZeroRequestInfo): App.PaymentScheduleChargeData | undefined => {
    const key = getPaymentScheduleKey(reserveForZeroRequestInfo)

    if (key && paymentSchedule.charges && key in paymentSchedule.charges) {
      return paymentSchedule.charges[key]
    }

    return undefined
  },
)

export const isReserveForZeroPaymentScheduleEligible = createSelector(
  getReserveForZeroPaymentSchedule,
  (reserveForZeroSchedule): boolean => reserveForZeroSchedule?.eligible ?? false,
)
